/**
 * student.info.customFilters supported shapes:
 *
 * 1) Flat (per org in getStudents payload): { [filterId]: assigned, ... }
 *    A truthy value means the learner has that custom filter assigned.
 *
 * 2) Nested: { [organizationId]: { [filterId]: assigned } }
 *
 * 3) Array: [{ [organizationId]: { filterId: value } }, ...] or
 *    [{ organizationId, filters | filterValues | ... }, ...]
 *
 * Legacy: info.customFilterValues: { [filterId]: tagValue }
 */

function normOrgId(id) {
  if (id == null) return '';
  return String(id).trim();
}

function normalizeInnerMap(inner) {
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return {};
  const out = {};
  for (const k of Object.keys(inner)) {
    out[String(k)] = inner[k];
  }
  return out;
}

/** True when object is { filterId: value, ... } (values are not plain objects). */
export function isFlatFilterIdToValueMap(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  for (const v of Object.values(obj)) {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      return false;
    }
  }
  return true;
}

function extractOrgMap(raw, organizationId) {
  const oid = normOrgId(organizationId);
  if (!oid || raw == null) return null;

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (raw[oid] != null && typeof raw[oid] === 'object' && !Array.isArray(raw[oid])) {
      return normalizeInnerMap(raw[oid]);
    }
    for (const k of Object.keys(raw)) {
      if (normOrgId(k) === oid) {
        const inner = raw[k];
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          return normalizeInnerMap(inner);
        }
      }
    }
    return null;
  }

  if (!Array.isArray(raw)) return null;

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;

    const declaredOrg =
      item.organizationId ?? item.companyID ?? item.orgId ?? item.organization_id;
    if (declaredOrg != null && normOrgId(declaredOrg) === oid) {
      const inner =
        item.filters ??
        item.filterValues ??
        item.values ??
        item.map ??
        item.customFilters;
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return normalizeInnerMap(inner);
      }
      return {};
    }

    for (const k of Object.keys(item)) {
      if (normOrgId(k) === oid) {
        const inner = item[k];
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          return normalizeInnerMap(inner);
        }
      }
    }
  }

  return null;
}

export function getOrgCustomFilterMap(student, organizationId) {
  if (!student || organizationId == null) return {};

  const info = student.info || {};
  const raw =
    info.customFilters ??
    student.customFilters ??
    info.user?.customFilters;

  // API returns flat { filterId: value } for learners in this org
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && isFlatFilterIdToValueMap(raw)) {
    return normalizeInnerMap(raw);
  }

  const extracted = extractOrgMap(raw, organizationId);
  if (extracted != null) {
    return extracted;
  }

  const legacy = info.customFilterValues;
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
    return normalizeInnerMap(legacy);
  }

  return {};
}

export function getTagValueForFilter(map, filterId) {
  if (!map || typeof map !== 'object') return undefined;
  const fid = String(filterId);
  if (fid in map) return map[fid];
  if (filterId in map) return map[filterId];
  for (const k of Object.keys(map)) {
    if (String(k) === fid) return map[k];
  }
  return undefined;
}

/** True when the learner has this custom filter assigned (any truthy stored value). */
export function studentHasCustomFilterAssigned(map, filterId) {
  const v = getTagValueForFilter(map, filterId);
  return v != null && v !== '' && v !== '__none__' && v !== false;
}

function ensureCustomFiltersArray(student) {
  let list = student.info.customFilters;

  if (Array.isArray(list)) {
    return list;
  }

  if (list && typeof list === 'object') {
    const arr = [];
    for (const k of Object.keys(list)) {
      const inner = list[k];
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        arr.push({ [normOrgId(k)]: normalizeInnerMap(inner) });
      }
    }
    student.info.customFilters = arr;
    return arr;
  }

  student.info.customFilters = [];
  return student.info.customFilters;
}

export function setOrgCustomFilterMap(student, organizationId, map) {
  if (!student?.info || organizationId == null) return;

  const next = map && typeof map === 'object' ? normalizeInnerMap({ ...map }) : {};
  Object.keys(next).forEach((k) => {
    if (next[k] === undefined || next[k] === null || next[k] === '') {
      delete next[k];
    }
  });

  const cur = student.info.customFilters;

  // Persist as flat { filterId: value } (matches getStudents / update API)
  if (
    cur == null ||
    (typeof cur === 'object' &&
      !Array.isArray(cur) &&
      (isFlatFilterIdToValueMap(cur) || Object.keys(cur).length === 0))
  ) {
    student.info.customFilters = { ...next };
    return;
  }

  const oid = normOrgId(organizationId);
  const list = ensureCustomFiltersArray(student);

  const idx = list.findIndex((item) => item && typeof item === 'object' && oid in item);

  if (idx === -1) {
    if (Object.keys(next).length > 0) {
      list.push({ [oid]: next });
    }
  } else if (Object.keys(next).length === 0) {
    list.splice(idx, 1);
  } else {
    list[idx] = { [oid]: next };
  }
}

export function patchOrgCustomFilterValue(student, organizationId, filterId, value) {
  const map = getOrgCustomFilterMap(student, organizationId);
  const cleared =
    value === undefined || value === null || value === '' || value === '__none__' || value === false;
  const fid = String(filterId);
  if (cleared) {
    delete map[fid];
    delete map[filterId];
  } else {
    map[fid] = value === true ? true : value;
  }
  setOrgCustomFilterMap(student, organizationId, map);
}

function normEmail(email) {
  if (email == null || email === '') return '';
  return String(email).trim().toLowerCase();
}

/** Normalize toolbar/report selections to an array of filter id strings. */
export function normalizeSelectedCustomFilterIds(selections) {
  if (Array.isArray(selections)) {
    return selections.map(String).filter(Boolean);
  }
  if (selections && typeof selections === 'object') {
    return Object.keys(selections).filter((k) => {
      const v = selections[k];
      return v != null && v !== '';
    }).map(String);
  }
  return [];
}

/** Match a report row (`user_email`, `user_id`, …) to a roster student from getStudents. */
export function findOrgStudentForReportRow(row, students) {
  if (!row || !Array.isArray(students) || !students.length) {
    return null;
  }
  const id = row.user_id ?? row.userId ?? row.id;
  if (id != null && id !== '') {
    const byId = students.find((s) => s?.info?.id != null && String(s.info.id) === String(id));
    if (byId) return byId;
  }
  const email = normEmail(row.user_email ?? row.email);
  if (!email) return null;
  return (
    students.find((s) => normEmail(s?.info?.email) === email) ||
    null
  );
}

export function hasActiveCustomTagSelections(selections, _filterDefs) {
  return normalizeSelectedCustomFilterIds(selections).length > 0;
}

export function studentMatchesCustomFilterSelection(student, organizationId, selectedFilterIds) {
  const ids = normalizeSelectedCustomFilterIds(selectedFilterIds);
  if (!ids.length) return true;
  const map = getOrgCustomFilterMap(student, organizationId);
  return ids.some((fid) => studentHasCustomFilterAssigned(map, fid));
}

/** Same rules as Manage Learners custom filter bar, for report `studentData` rows. */
export function reportRowMatchesCustomTagFilters(
  row,
  students,
  organizationId,
  selections,
  _filterDefs
) {
  const ids = normalizeSelectedCustomFilterIds(selections);
  if (!ids.length) return true;
  const student = findOrgStudentForReportRow(row, students);
  if (!student) return false;
  return studentMatchesCustomFilterSelection(student, organizationId, ids);
}

export function applyCustomTagFilterToReportRows(
  rows,
  students,
  organizationId,
  selections,
  filterDefs
) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) =>
    reportRowMatchesCustomTagFilters(row, students, organizationId, selections, filterDefs)
  );
}

/** Same rules as Manage Learners custom filter bar, for roster `students` rows. */
export function applyCustomTagFilterToStudents(
  students,
  organizationId,
  selections,
  filterDefs
) {
  const list = Array.isArray(students) ? students : [];
  const ids = normalizeSelectedCustomFilterIds(selections);
  if (!ids.length) return list;
  const orgId = normOrgId(organizationId);
  return list.filter((s) => studentMatchesCustomFilterSelection(s, orgId, ids));
}

/** Emails of roster learners that pass active custom filters (org-wide report tabs). */
export function filteredRosterLearnerEmailSet(students, organizationId, selections, filterDefs) {
  const filtered = applyCustomTagFilterToStudents(
    students,
    organizationId,
    selections,
    filterDefs
  );
  const emails = new Set();
  for (const s of filtered) {
    const e = normEmail(s?.info?.email);
    if (e) emails.add(e);
  }
  return emails;
}

/** Emails of learners that pass the active custom filters (for activity / completion tabs). */
export function filteredReportLearnerEmailSet(rows, students, organizationId, selections, filterDefs) {
  const filtered = applyCustomTagFilterToReportRows(
    rows,
    students,
    organizationId,
    selections,
    filterDefs
  );
  const emails = new Set();
  for (const row of filtered) {
    const e = normEmail(row.user_email ?? row.email);
    if (e) emails.add(e);
  }
  return emails;
}

/** Sort report rows by learner display name (same rules as Manage Learners). */
export function sortReportRowsByLearnerName(rows, sortBy = 'firstName') {
  const list = Array.isArray(rows) ? [...rows] : [];
  return list.sort((a, b) => {
    const nameA = (a.user_name ?? a.name ?? '').trim();
    const nameB = (b.user_name ?? b.name ?? '').trim();
    if (sortBy === 'firstName') {
      const firstA = (nameA.split(' ')[0] || '').toLowerCase();
      const firstB = (nameB.split(' ')[0] || '').toLowerCase();
      return firstA.localeCompare(firstB);
    }
    const partsA = nameA.split(' ');
    const partsB = nameB.split(' ');
    const lastA = (partsA[partsA.length - 1] || '').toLowerCase();
    const lastB = (partsB[partsB.length - 1] || '').toLowerCase();
    return lastA.localeCompare(lastB);
  });
}
